"use client";

import React, { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { validateKeyValuePair } from "@/lib/utils/json-field-utils";
import type { JsonValue } from "@/components/shared/types";

interface AddKeyValueFormProps {
  onAdd: (key: string, value: JsonValue) => void;
  existingKeys: string[];
  isLoading?: boolean;
}

/**
 * Form to add new key-value pairs
 * Includes validation and error display
 */
export function AddKeyValueForm({ onAdd, existingKeys, isLoading = false }: AddKeyValueFormProps) {
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleAdd = useCallback(() => {
    setError(null);

    // Validate key
    if (!newKey.trim()) {
      setError("Key cannot be empty");
      return;
    }

    // Check for duplicate keys
    if (existingKeys.includes(newKey)) {
      setError("Key already exists");
      return;
    }

    // Try to parse value
    let parsedValue: JsonValue;
    try {
      parsedValue = JSON.parse(newValue) as JsonValue;
    } catch {
      // If it's not valid JSON, treat it as a string
      parsedValue = newValue;
    }

    // Validate the pair
    const validation = validateKeyValuePair(newKey, parsedValue);
    if (!validation.valid) {
      setError(validation.error || "Invalid key-value pair");
      return;
    }

    onAdd(newKey, parsedValue);
    setNewKey("");
    setNewValue("");
  }, [newKey, newValue, existingKeys, onAdd]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleAdd();
      }
    },
    [handleAdd]
  );

  return (
    <div className="space-y-2 p-3 bg-muted rounded-md border border-dashed border-input">
      <p className="text-xs font-semibold text-muted-foreground">Add New Pair</p>

      <div className="grid grid-cols-2 gap-2">
        <Input
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Key"
          className="text-sm"
          disabled={isLoading}
        />
        <Input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Value (JSON or string)"
          className="text-sm"
          disabled={isLoading}
        />
      </div>

      {error && <p className="text-xs text-destructive font-medium">{error}</p>}

      <Button
        size="sm"
        onClick={handleAdd}
        disabled={isLoading || !newKey.trim()}
        className="w-full h-8"
      >
        <Plus className="h-3 w-3 mr-1" />
        Add Pair
      </Button>
    </div>
  );
}
